'use client';

import { Field } from '@base-ui/react/field';
import { Input } from '@base-ui/react/input';
import clsx from 'clsx';
import type {
  FocusEvent,
  HTMLAttributes,
  HTMLInputTypeAttribute,
  KeyboardEventHandler,
  ReactElement,
  Ref,
} from 'react';
import { useId, useState } from 'react';
import { Eye, EyeSlash } from 'react-bootstrap-icons';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSIconButton } from '@repo/sds/components/SDSIconButton';
import { SDSInputErrorMessage } from '@repo/sds/components/SDSInputErrorMessage';
import type { SDSTextType } from '@repo/sds/components/SDSText';
import { SDSText } from '@repo/sds/components/SDSText';

import '@repo/sds/components/SDSTextInput/SDSTextInput.scss';

export interface CommonSDSTextInputProps {
  errorMessage?: string;
  errorMessageTestId?: string;
  inputClassName?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  isReadOnly?: boolean;
  /** Browser autofill hint (WCAG 1.3.5), e.g. `given-name` or `email`. */
  autoComplete?: string;
  /** Virtual keyboard hint, e.g. `email`. The input element's own type stays `text` for non-password fields. */
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  name?: string;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onChange?: (value: string) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  type?: HTMLInputTypeAttribute;
  value?: string;
  wrapperClassName?: string;
  isFullWidth?: boolean;
  error?: boolean;
  textType?: SDSTextType;
  /** Typography of the label above the field; defaults to the inherited text style. */
  labelTextType?: SDSTextType;
  leftIcon?: ReactElement;
  rightIcon?: ReactElement;
  prefix?: string;
  dataTestId?: string;
  numeric?: boolean;
  maxLength?: number;
  ariaDescribedBy?: string;
  ariaLabelledBy?: string;
  id?: string;
  labelIsHidden?: boolean;
  ref?: Ref<HTMLInputElement>;
}

type WithLabel = CommonSDSTextInputProps & {
  label: string;
  placeholder?: string;
  ariaLabel?: string;
};

type WithoutLabel = CommonSDSTextInputProps & {
  label?: never;
  placeholder: string;
  ariaLabel: string;
};

export type SDSTextInputProps = WithLabel | WithoutLabel;

const PasswordToggle = ({
  isPasswordVisible,
  isDisabled,
  onToggle,
}: {
  isPasswordVisible: boolean;
  isDisabled?: boolean;
  onToggle: () => void;
}) => (
  <SDSIconButton
    size="medium"
    dataTestId={DataTestIds.TEXT_INPUT.SHOW_PASSWORD}
    icon={!isPasswordVisible ? <Eye /> : <EyeSlash />}
    onClick={onToggle}
    isDisabled={isDisabled}
    ariaLabel={isPasswordVisible ? 'Hide password' : 'Show password'}
  />
);

// Custom hook for password visibility management
const usePasswordToggle = (isDisabled?: boolean) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const togglePasswordVisibility = () => {
    if (!isDisabled) {
      setIsPasswordVisible(prev => !prev);
    }
  };

  return { isPasswordVisible, togglePasswordVisibility };
};

// Utility function for numeric validation
const isValidNumericInput = (value: string): boolean => {
  // No leading zeros
  if (value.length === 1 && value.startsWith('0')) {
    return false;
  }

  // Last character must be a digit
  const lastSymbol = value.at(-1);
  if (lastSymbol && !/\d$/.test(lastSymbol)) {
    return false;
  }

  return true;
};

// Custom hook for input change handling with Base UI integration
const useInputHandler = (onChange?: (value: string) => void, numeric?: boolean) => {
  const handleValueChange = (value: string | number | readonly string[] | undefined) => {
    // Convert the value to string - Base UI can pass different types
    const stringValue = String(value ?? '');

    if (stringValue.length === 0) {
      onChange?.('');
      return;
    }

    if (!numeric || isValidNumericInput(stringValue)) {
      onChange?.(stringValue);
    }
  };

  return handleValueChange;
};

// Helper to determine input type
const getInputType = (type: string, isPasswordVisible: boolean, isDisabled?: boolean): string => {
  if (type === 'password' && (!isPasswordVisible || isDisabled)) {
    return 'password';
  }
  return 'text';
};

const useInputClassNames = ({
  isFullWidth,
  errorMessage,
  isDisabled,
  wrapperClassName,
  inputClassName,
  textType,
}: {
  isFullWidth?: boolean;
  errorMessage?: string;
  isDisabled?: boolean;
  wrapperClassName?: string;
  inputClassName?: string;
  textType: string;
}) => {
  const fieldClassName = clsx('SDSTextInput__field', { 'SDSTextInput--fullWidth': isFullWidth }, wrapperClassName);

  const inputWrapperClassName = clsx(
    'SDSTextInput',
    {
      'SDSTextInput--error': errorMessage,
      'SDSTextInput--fullWidth': isFullWidth,
      'SDSTextInput--disabled': isDisabled,
    },
    inputClassName,
  );

  const textInputClassName = `SDSText--${textType}`;
  const combinedInputClassName = clsx('SDSTextInput__input', textInputClassName);

  return { fieldClassName, inputWrapperClassName, combinedInputClassName };
};

// Helper components for better organization
const InputDecorations = ({
  leftIcon,
  prefix,
  textType,
}: {
  leftIcon?: ReactElement;
  prefix?: string;
  textType: string;
}) => (
  <>
    {leftIcon && <span className="SDSTextInput__icon">{leftIcon}</span>}
    {/* Match the input's own text styling - without it the prefix falls back to the
        ambient page font size, so it renders taller than the value next to it. */}
    {prefix && <span className={clsx('SDSTextInput__prefix', `SDSText--${textType}`)}>{prefix}</span>}
  </>
);

// Renders the optional field label, including the required-field marker.
const SDSTextInputLabel = ({
  label,
  isRequired,
  labelIsHidden,
  labelTextType,
  inputId,
}: {
  label?: string;
  isRequired?: boolean;
  labelIsHidden: boolean;
  labelTextType?: SDSTextType;
  inputId: string;
}) => {
  if (!label) {
    return null;
  }
  return (
    <Field.Label
      className={clsx('SDSInputLabel', {
        'SDSInputLabel--visuallyHidden': labelIsHidden,
      })}
      htmlFor={inputId}
    >
      {labelTextType ? <SDSText text={label} type={labelTextType} as="span" color="inherit" isInline /> : label}
      {isRequired && <span className="SDSInputLabel__required">*</span>}
    </Field.Label>
  );
};

// Builds the prop object passed to Base UI's Input, isolating the small
// amount of derivation (combined aria-describedby, data-testid fallback)
// from the rest of the component.
function buildInputProps(options: {
  combinedInputClassName: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  placeholder?: string;
  value: string;
  handleValueChange: (value: string | number | readonly string[] | undefined) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  name?: string;
  inputType: string;
  maxLength?: number;
  autoComplete?: string;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  inputId: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  errorId?: string;
  ariaLabelledBy?: string;
  dataTestId?: string;
}) {
  return {
    className: options.combinedInputClassName,
    disabled: options.isDisabled,
    readOnly: options.isReadOnly,
    placeholder: options.placeholder,
    value: options.value ?? '',
    onValueChange: options.handleValueChange,
    onBlur: options.onBlur,
    onKeyDown: options.onKeyDown,
    name: options.name,
    type: options.inputType,
    maxLength: options.maxLength,
    autoComplete: options.autoComplete,
    inputMode: options.inputMode,
    id: options.inputId,
    'aria-label': options.ariaLabel,
    'aria-describedby': [options.ariaDescribedBy, options.errorId].filter(Boolean).join(' ') || undefined,
    'aria-labelledby': options.ariaLabelledBy,
    'data-testid': options.dataTestId ?? `${options.name}-input`,
  };
}

const InputSuffixes = ({
  rightIcon,
  showPasswordToggle,
  isPasswordVisible,
  isDisabled,
  onTogglePassword,
}: {
  rightIcon?: ReactElement;
  showPasswordToggle: boolean;
  isPasswordVisible: boolean;
  isDisabled?: boolean;
  onTogglePassword: () => void;
}) => (
  <>
    {rightIcon && <span className="SDSTextInput__rightIcon">{rightIcon}</span>}
    {showPasswordToggle && (
      <PasswordToggle isPasswordVisible={isPasswordVisible} isDisabled={isDisabled} onToggle={onTogglePassword} />
    )}
  </>
);

export function SDSTextInput({
  errorMessage,
  errorMessageTestId,
  inputClassName,
  textType = 'body-medium',
  labelTextType,
  isDisabled,
  isRequired,
  isReadOnly,
  autoComplete,
  inputMode,
  label,
  name,
  onBlur,
  onChange,
  onKeyDown,
  placeholder,
  type = 'text',
  value = '',
  wrapperClassName,
  isFullWidth,
  leftIcon,
  rightIcon,
  prefix,
  dataTestId,
  numeric = false,
  maxLength,
  ariaLabel,
  ariaDescribedBy,
  ariaLabelledBy,
  id,
  labelIsHidden = false,
  ref,
}: SDSTextInputProps) {
  const generatedId = useId();
  const inputId = id || generatedId;
  const errorId = errorMessage ? `${inputId}-error` : undefined;

  const { isPasswordVisible, togglePasswordVisibility } = usePasswordToggle(isDisabled);
  const handleValueChange = useInputHandler(onChange, numeric);

  const { fieldClassName, inputWrapperClassName, combinedInputClassName } = useInputClassNames({
    isFullWidth,
    errorMessage,
    isDisabled,
    wrapperClassName,
    inputClassName,
    textType,
  });

  const inputType = getInputType(type, isPasswordVisible, isDisabled);
  const showPasswordToggle = type === 'password';

  const inputProps = buildInputProps({
    combinedInputClassName,
    isDisabled,
    isReadOnly,
    placeholder,
    value,
    handleValueChange,
    onBlur,
    onKeyDown,
    name,
    inputType,
    maxLength,
    autoComplete,
    inputMode,
    inputId,
    ariaLabel,
    ariaDescribedBy,
    errorId,
    ariaLabelledBy,
    dataTestId,
  });

  return (
    <Field.Root className={fieldClassName} disabled={isDisabled} name={name} invalid={!!errorMessage}>
      <SDSTextInputLabel
        label={label}
        isRequired={isRequired}
        labelIsHidden={labelIsHidden}
        labelTextType={labelTextType}
        inputId={inputId}
      />

      <div className={inputWrapperClassName}>
        <InputDecorations leftIcon={leftIcon} prefix={prefix} textType={textType} />
        <Input {...inputProps} ref={ref} />
        <InputSuffixes
          rightIcon={rightIcon}
          showPasswordToggle={showPasswordToggle}
          isPasswordVisible={isPasswordVisible}
          isDisabled={isDisabled}
          onTogglePassword={togglePasswordVisibility}
        />
      </div>

      <SDSInputErrorMessage id={errorId} errorMessage={errorMessage} dataTestId={errorMessageTestId} />
    </Field.Root>
  );
}
